/**
 * The upload driver for a stand-alone chat's photo: a `File` in, a media id
 * ready to hand to `/chat/create-standalone` or `/chat/update`, a photo
 * waiting on a moderation result, or a retryable failure out.
 *
 * Adapted from `apps/app/src/features/profile/useAvatarUpload.ts`, stopping
 * after `/upload/complete`: there is no `avatar/set` equivalent for a chat
 * photo. The `mediaId` this hook produces is handed to the caller's own
 * `/chat/create-standalone` or `/chat/update` call instead.
 *
 * `/upload/complete` is synchronous and terminal, exactly as the avatar
 * template documents: nothing here polls.
 */
import { FILE_TOO_LARGE_COPY } from "@sportsmet/errors";
import { useUploadComplete, useUploadInit } from "@sportsmet/media-sdk/private";
import { useCallback, useRef, useState } from "react";

import { photoUploadRefusalMessage } from "../../errors/apiErrorBus";

export const MAX_CHAT_PHOTO_BYTES = 15_000_000;

export const CHAT_PHOTO_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
] as const;

export type typeChatPhotoContentType =
  (typeof CHAT_PHOTO_CONTENT_TYPES)[number];

export type typeChatPhotoUploadState =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "ready"; mediaId: string }
  | { kind: "pendingReview" }
  | { kind: "failed"; message: string | null };

export function useChatPhotoUpload(): {
  state: typeChatPhotoUploadState;
  uploadPhoto: (file: File) => void;
  reset: () => void;
} {
  const uploadInit = useUploadInit();
  const uploadComplete = useUploadComplete();
  const [state, setState] = useState<typeChatPhotoUploadState>({
    kind: "idle",
  });
  // The re-entrancy guard lives on a ref, not on `state`, so the check inside
  // `run` always sees the latest value rather than whatever `state` was when
  // the enclosing callback was created.
  const isUploadingRef = useRef(false);

  const uploadPhoto = useCallback(
    (file: File) => {
      // 1. Re-entrancy guard. One upload at a time.
      if (isUploadingRef.current) return;

      async function run(): Promise<void> {
        // 2. Client-side pre-checks, before any network call. These mirror
        // the server's own refusals (UNSUPPORTED_CONTENT_TYPE and
        // FILE_TOO_LARGE) so an oversized or wrong-type file costs the fan a
        // local check instead of a round trip. The server still refuses
        // independently; this is a courtesy gate, never the real one.
        if (
          !(CHAT_PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)
        ) {
          setState({ kind: "failed", message: null });
          return;
        }
        // An empty file and an oversized one are two different refusals. Only
        // the oversized one is a size problem, so only it gets the size
        // sentence (ISSUE-839).
        if (file.size > MAX_CHAT_PHOTO_BYTES) {
          setState({ kind: "failed", message: FILE_TOO_LARGE_COPY });
          return;
        }
        if (file.size < 1) {
          setState({ kind: "failed", message: null });
          return;
        }

        // 3. Mint the request id once and reuse it for the rest of this call.
        isUploadingRef.current = true;
        setState({ kind: "uploading" });
        const requestId = crypto.randomUUID();

        // 4. /upload/init
        let init;
        try {
          init = await uploadInit.mutateAsync({
            purpose: "chatPhoto",
            contentType: file.type as typeChatPhotoContentType,
            byteSize: file.size,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (init.error != null || init.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(init),
          });
          return;
        }

        // 5. The presigned PUT. The browser does this itself with a bare
        // fetch, never through any SDK: the URL is presigned and points at
        // object storage, not at a sportsmet service, so it carries no
        // session header and no browser-managed session cookies. The whole
        // File is the body; no FormData, no multipart, no chunking.
        let putResponse: Response;
        try {
          putResponse = await fetch(init.data.uploadUrl, {
            method: init.data.uploadMethod,
            headers: { "content-type": init.data.uploadHeaders.contentType },
            body: file,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (!putResponse.ok) {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }

        // 6. /upload/complete. Its status has exactly three possible values.
        let complete;
        try {
          complete = await uploadComplete.mutateAsync({
            mediaId: init.data.mediaId,
            requestId,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (complete.error != null || complete.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(complete),
          });
          return;
        }
        if (
          complete.data.status === "pendingReview" ||
          complete.data.status === "heldOnBudget"
        ) {
          // The photo uploaded fine and is not viewable yet. Not a failure,
          // not a rejection. The chat is created or updated with its
          // initials plate instead until moderation clears it.
          isUploadingRef.current = false;
          setState({ kind: "pendingReview" });
          return;
        }

        isUploadingRef.current = false;
        setState({ kind: "ready", mediaId: complete.data.mediaId });
      }

      void run();
    },
    [uploadInit, uploadComplete],
  );

  const reset = useCallback(() => {
    setState({ kind: "idle" });
  }, []);

  return { state, uploadPhoto, reset };
}
